/**
 * Cloud Agent — turning server `status` frames into one plain user-facing line. Pure, React-free.
 *
 * The server's status text is written for the model, not the user: a tool step arrives as
 * `Analyzing: <the computation's model-facing description>`, and those descriptions are long,
 * multi-sentence, and end with block-type markers ("… KPI.", "… Table, ex-VAT.") plus prompting
 * hints ("Use it for 'what hurt margin' …"). Rendering that verbatim reads like debug output.
 *
 * This trims it down CLIENT-SIDE — the server text is deliberately left alone, since it is tuned
 * for the model's tool selection and changing it would change the agent's behaviour.
 */

/** The one prefix the server puts in front of a computation description. */
const ANALYZING_PREFIX = /^analy[sz]ing\s*:\s*/i;

/**
 * Trailing block-type markers, e.g. "… spend. Table, ex-VAT." Only stripped from the END, so a
 * description that legitimately mentions a table mid-sentence keeps it.
 */
const TRAILING_BLOCK_MARKER =
  /[\s.,;–—-]*\b(kpi|kpis|table|chart|line ?chart|bar ?chart|rank ?bars|donut|scatter)\b(\s*,?\s*ex-vat)?\s*\.?\s*$/i;

/** Longest line we will show before eliding — roughly one comfortable row on a phone. */
const MAX_LENGTH = 80;

/** Uppercase the first letter, leaving the rest alone (so "VAT", "€" and names survive). */
function sentenceCase(text: string): string {
  return text.length === 0 ? text : text[0].toUpperCase() + text.slice(1);
}

/** Cut to `MAX_LENGTH` on a word boundary where possible. */
function truncate(text: string): string {
  if (text.length <= MAX_LENGTH) return text;
  const cut = text.slice(0, MAX_LENGTH);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > MAX_LENGTH * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:–—-]+$/, '');
}

/**
 * One status frame → one plain sentence, always ending in an ellipsis so it reads as in-progress.
 * Returns '' for empty input so callers can fall back to their own "thinking" copy.
 */
export function humaniseStatus(raw: string): string {
  const input = (raw ?? '').trim();
  if (!input) return '';

  let text = input.replace(ANALYZING_PREFIX, '').trim();

  // Keep only the first sentence: everything after it is model-prompting detail. A period is a
  // sentence end only when followed by whitespace, so "ex-VAT." and decimals stay intact.
  const sentenceEnd = text.search(/\.\s/);
  if (sentenceEnd !== -1) text = text.slice(0, sentenceEnd);

  text = text
    .replace(/\s*\.\s*$/, '')
    .replace(TRAILING_BLOCK_MARKER, '')
    .replace(/[\s.,;:–—-]+$/, '')
    .trim();

  if (!text) return '';
  const body = sentenceCase(truncate(text));
  // Server statuses like "Reading X data…" already end in one — appending would double it.
  return /…$/.test(body) ? body : `${body}…`;
}

/**
 * The line shown while a turn is working: the latest step, or '' when no step has arrived yet
 * (the caller shows its own translated "Thinking…" then).
 */
export function currentActivityLine(status: string[]): string {
  for (let i = status.length - 1; i >= 0; i -= 1) {
    const line = humaniseStatus(status[i]);
    if (line) return line;
  }
  return '';
}

/** Every step humanised, for the expanded list. Empty results are dropped. */
export function humanisedSteps(status: string[]): string[] {
  return status.map(humaniseStatus).filter(Boolean);
}

/**
 * Whole seconds a turn took, from its start to its end (or to `now` while it runs).
 * Never negative, so a clock adjustment mid-turn cannot render "-3s".
 */
export function elapsedSeconds(startedAt: number, endedAt: number | null, now: number): number {
  return Math.max(0, Math.round(((endedAt ?? now) - startedAt) / 1000));
}
